import { getAuthority, getFeatureUsages } from '@modusign/openapi-client-subscription';
import { all, delay, put, retry, takeLatest } from 'redux-saga/effects';

import { REPLICATION_DELAY } from '@app/constants/delayTime';
import { payInvoice } from '@app/features/payment';
import {
  activateSubscription,
  buyProduct,
  changeSubscription,
  createSubscription,
  createSubscriptionSuccess,
  pauseSubscription,
  resumeSubscription,
  startTrialPlanSuccess,
  startTrialWithSurveySuccess,
} from '@app/features/subscription/state';
import workspaceService from '@app/libs/workspaceService';
import { signInSuccess } from '@app/store/account/local/actions';
import { thirdPartySignInSuccess } from '@app/store/account/third-party/actions';
import {
  CONTRACT_SETUP_SUBMITTED,
  DOCUMENT_PROGRESS__SIGNING_REMIND_SUCCESS,
  SUBSCRIPTION_TRIAL_PLAN_START_SUCCESS,
  SUBSCRIPTION_TRIAL_PLAN_START_WITH_SURVEY_SUCCESS,
  USER_AUTHORITY__FETCH_REQUEST,
  USER_AUTHORITY__PERMISSIONS_AND_LIMITS_FETCH_REQUEST,
} from '@app/store/actionTypes';
import { contractSetupSubmitted } from '@app/store/contract-setup/actions';
import { remindSigningSuccess } from '@app/store/documentProgress/actions';
import {
  fetchUserAuthority,
  fetchUserAuthorityFailure,
  fetchUserAuthorityPermissionsAndLimits,
  fetchUserAuthorityPermissionsAndLimitsFailure,
  fetchUserAuthorityPermissionsAndLimitsSuccess,
  fetchUserAuthoritySuccess,
} from '@app/store/user/authority/actions';

export function* fetchUserAuthorityPermissionsAndLimitsSaga(
  action: ReturnType<
    | typeof startTrialPlanSuccess
    | typeof startTrialWithSurveySuccess
    | typeof fetchUserAuthorityPermissionsAndLimits
  >
) {
  try {
    if (action.type !== USER_AUTHORITY__PERMISSIONS_AND_LIMITS_FETCH_REQUEST) {
      yield delay(REPLICATION_DELAY);
    }

    const data: Awaited<ReturnType<typeof getAuthority>> = yield retry(
      3,
      2 * 1000,
      getAuthority,
      workspaceService.getWorkspaceId()
    );
    yield put(fetchUserAuthorityPermissionsAndLimitsSuccess(data));
  } catch (error) {
    yield put(fetchUserAuthorityPermissionsAndLimitsFailure(error));
  }
}

export function* fetchUserAuthoritySaga({
  type,
}:
  | ReturnType<typeof contractSetupSubmitted>
  | ReturnType<typeof fetchUserAuthority>
  | ReturnType<typeof remindSigningSuccess>
  | ReturnType<typeof signInSuccess>
  | ReturnType<typeof thirdPartySignInSuccess>) {
  try {
    if (type !== USER_AUTHORITY__FETCH_REQUEST) {
      yield delay(REPLICATION_DELAY);
    }
    const data: Awaited<ReturnType<typeof getFeatureUsages>> = yield retry(
      3,
      2 * 1000,
      getFeatureUsages,
      workspaceService.getWorkspaceId()
    );
    yield put(fetchUserAuthoritySuccess(data));
  } catch (error) {
    yield put(fetchUserAuthorityFailure(error));
  }
}

export default function* sagas() {
  yield all([
    takeLatest(
      [
        SUBSCRIPTION_TRIAL_PLAN_START_SUCCESS,
        SUBSCRIPTION_TRIAL_PLAN_START_WITH_SURVEY_SUCCESS,
        USER_AUTHORITY__PERMISSIONS_AND_LIMITS_FETCH_REQUEST,
        activateSubscription.fulfilled.type,
        buyProduct.fulfilled.type,
        changeSubscription.fulfilled.type,
        createSubscription.fulfilled.type,
        createSubscriptionSuccess,
        pauseSubscription.fulfilled.type,
        resumeSubscription.fulfilled.type,
        payInvoice.fulfilled.type,
      ],
      fetchUserAuthorityPermissionsAndLimitsSaga
    ),
    takeLatest(
      [
        CONTRACT_SETUP_SUBMITTED,
        DOCUMENT_PROGRESS__SIGNING_REMIND_SUCCESS,
        USER_AUTHORITY__FETCH_REQUEST,
        signInSuccess.type,
        thirdPartySignInSuccess.type,
      ],
      fetchUserAuthoritySaga
    ),
  ]);
}
